/* The pieces the student sections are built from.

   These lived in AdminStudentDetail.js while that page was the only thing that
   rendered a student. The admissions page mirrors those sections now, so the
   helpers, the option lists and the two finance cards live beside the sections
   that use them, and the student page imports them back.

   Nothing here is new: it is the same code, moved. */

import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { courseLabel } from '@/components/admin/AcademicTableHelpers';
import {
  addCalendarDays,
  formatDateTimeValue,
  formatDisplayDate,
  parseDateValue,
} from '@/lib/dates';

export const GOLD = '#D4AF37';

// Radix reads an empty select value as "no value", so "not set" needs a
// sentinel of its own. Shared with the student page's form.
export const NONE_VALUE = '__none__';

export function safeList(value) {
  return Array.isArray(value) ? value : [];
}

export function numberValue(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
}

export function formatCurrency(v) {
  const amount = Number(v);
  return '$' + (Number.isFinite(amount) ? amount : 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function graduationPercent(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const start = parseDateValue(startDate)?.getTime() ?? Number.NaN;
  const end = parseDateValue(endDate)?.getTime() ?? Number.NaN;
  const now = Date.now();
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null;
  const pct = ((now - start) / (end - start)) * 100;
  return Math.min(100, Math.max(0, Math.round(pct)));
}

export function formatDate(value) {
  return formatDisplayDate(value, { fallback: 'Not set' });
}

export function computeNotificationDueDate(endDate) {
  return addCalendarDays(endDate, -30);
}

export function displayText(value, fallback = 'Not set') {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return fallback;
}

export function courseBadgeLabel(course, courseMap = {}) {
  if (typeof course === 'string') {
    const resolved = courseMap[course];
    return resolved ? courseLabel(resolved) : UNKNOWN_COURSE_LABEL;
  }
  if (typeof course === 'number') return String(course);
  if (course && typeof course === 'object') {
    const resolved = course.id ? courseMap[course.id] : null;
    if (resolved) return courseLabel(resolved);
    if (course.title || course.course_code) return courseLabel(course);
    return displayText(course.name, UNKNOWN_COURSE_LABEL);
  }
  return 'Course';
}

export function courseBadgeKey(course, index) {
  if (typeof course === 'string' || typeof course === 'number') return `${course}-${index}`;
  if (course && typeof course === 'object') return `${course.id || course.title || 'course'}-${index}`;
  return `course-${index}`;
}

export function dateChangeLabel(item) {
  if (item?.label) return item.label;
  return item?.field === 'end_date' ? 'End Date' : 'Start Date';
}

export function formatDateTime(value) {
  return formatDateTimeValue(value, { fallback: '' });
}

export const yesNoOptions = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

export const notificationStatuses = [
  { value: 'not_needed', label: 'Not needed' },
  { value: 'pending', label: 'Pending' },
  { value: 'sent', label: 'Sent' },
  { value: 'acknowledged', label: 'Acknowledged' },
];

export const documentStatuses = [
  { value: 'not_required', label: 'Not required' },
  { value: 'pending', label: 'Pending' },
  { value: 'verified', label: 'Verified' },
  { value: 'expiring_soon', label: 'Expiring soon' },
  { value: 'expired', label: 'Expired' },
  { value: 'missing', label: 'Missing' },
];

export const depositStatuses = [
  { value: 'not_required', label: 'Not required' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'waived', label: 'Waived' },
];

export function metaOptions(list) {
  return [{ value: NONE_VALUE, label: 'Not set' }, ...safeList(list).map((value) => ({ value, label: value }))];
}

export function FinanceChart({ data, colors, label1, label2, val1, val2 }) {
  const chartData = safeList(data).map((entry) => ({
    ...entry,
    value: numberValue(entry?.value),
  }));
  if (!chartData[0]?.value && !chartData[1]?.value) return <p className="text-sm text-[#062B1A]/50">No data</p>;
  return (
    <div className="flex flex-col items-center">
      <ResponsiveContainer width={180} height={180}>
        <PieChart>
          <Pie data={chartData} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={2}>
            {chartData.map((_, i) => <Cell key={i} fill={colors[i]} />)}
          </Pie>
          <Tooltip formatter={(v) => formatCurrency(v)} />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex gap-4 mt-2 text-xs">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: colors[0] }} /> {label1} {formatCurrency(val1)}</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: colors[1] }} /> {label2} {formatCurrency(val2)}</span>
      </div>
    </div>
  );
}

export { default as BillingSection } from '@/components/admin/StudentBillingCard';
